/* === Sage Custom Code Start (Apr 2026) === */
/* Added swatches overflow fix and desktop hover quick add syncs and auto add to cart function and fullview syncing */


import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */

  getSelectedVariantId() {

  const checkedInput = (
    this.querySelector(
      'input[type="radio"][data-variant-id]:checked'
    )
  );

  if (checkedInput?.dataset.variantId) {
    return checkedInput.dataset.variantId;
  }

  const selectedOption =
    this.variantPicker?.selectedOption;

  if (selectedOption?.dataset?.variantId) {
    return selectedOption.dataset.variantId;
  }

  if (selectedOption?.value) {
    return selectedOption.value;
  }

  return null;
}

syncFullviewSelectedColor() {

  const checkedInput =
    this.querySelector(
      'swatches-variant-picker-component input[type="radio"][data-variant-id]:checked'
    );

  if (!checkedInput) {

    console.log(
      '[Fullview Sync] NO CHECKED SWATCH'
    );

    return;
  }

  const selectedColor =
    String(
      checkedInput.value || ''
    ).trim();

  const selectedVariantId =
    String(
      checkedInput.dataset.variantId || ''
    ).trim();

  console.log(
    '[Fullview Sync] COLOR:',
    selectedColor
  );

  console.log(
    '[Fullview Sync] VARIANT:',
    selectedVariantId
  );

  if (!selectedColor || !selectedVariantId) {
    return;
  }

  /*
   * =========================================
   * REMEMBER THE PREVIOUS SELECTION
   * FOR THIS PRODUCT CARD ONLY
   * =========================================
   */

  const previousColor =
    String(
      this.dataset.fullviewPreviousColor || ''
    ).trim();

  const previousVariantId =
    String(
      this.dataset.fullviewPreviousVariantId || ''
    ).trim();

  /*
   * =========================================
   * DETECT A REAL COLOR CHANGE
   * =========================================
   */

  const colorChanged =
    previousColor &&
    selectedColor.toLowerCase() !==
      previousColor.toLowerCase();

  /*
   * =========================================
   * CLEAR ONLY THE SAVED STATE BELONGING
   * TO THIS PRODUCT CARD'S PREVIOUS VARIANT
   * =========================================
   */

  if (
    colorChanged &&
    previousVariantId &&
    typeof window.clearAluoviFullviewOpeningStateByVariant === 'function'
  ) {

    console.log(
      '[Fullview Sync] SWATCH CHANGED:',
      {
        from: previousColor,
        to: selectedColor,
        previousVariantId: previousVariantId,
        newVariantId: selectedVariantId
      }
    );

    window.clearAluoviFullviewOpeningStateByVariant(
      previousVariantId
    );

  }

  /*
   * =========================================
   * SAVE THIS CARD'S CURRENT SELECTION
   * =========================================
   *
   * These values are stored directly on
   * THIS product-card element.
   *
   * They do not affect other product cards.
   */

  this.dataset.fullviewPreviousColor =
    selectedColor;

  this.dataset.fullviewPreviousVariantId =
    selectedVariantId;

const hoverFullviewButton =
  this.querySelector(
    '.hover-fullview-button'
  );

const mobileFullviewButton =
  document.querySelector(
    '.quick-add-fullview-button'
  );

if (!hoverFullviewButton && !mobileFullviewButton) {

  console.log(
    '[Fullview Sync] NO FULLVIEW BUTTON YET — CURRENT CARD STATE SAVED LOCALLY'
  );

  return;
}

/*
 * =========================================
 * DESKTOP HOVER FULLVIEW
 * =========================================
 */

if (hoverFullviewButton) {

  hoverFullviewButton.dataset.selectedColor =
    selectedColor;

  hoverFullviewButton.dataset.selectedVariantId =
    selectedVariantId;

  console.log(
    '[Fullview Sync] DESKTOP HOVER BUTTON UPDATED:',
    {
      color:
        hoverFullviewButton.dataset.selectedColor,

      variantId:
        hoverFullviewButton.dataset.selectedVariantId
    }
  );

}

/*
 * =========================================
 * MOBILE QUICK ADD FULLVIEW
 * =========================================
 */

if (mobileFullviewButton) {

  mobileFullviewButton.dataset.selectedColor =
    selectedColor;

  mobileFullviewButton.dataset.selectedVariantId =
    selectedVariantId;

  console.log(
    '[Fullview Sync] MOBILE QUICK ADD BUTTON UPDATED:',
    {
      color:
        mobileFullviewButton.dataset.selectedColor,

      variantId:
        mobileFullviewButton.dataset.selectedVariantId
    }
  );

}

}

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    this.addEventListener(
  ThemeEvents.variantUpdate,
  this.#syncHoverSizePicker.bind(this)
);

requestAnimationFrame(() => {

  this.#syncHoverSizePicker();

  this.syncFullviewSelectedColor();

});

    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

this.addEventListener('click', (e) => {
  const btn = e.target.closest('.hover-size-picker__btn');
  if (!btn) return;

  // ❌ disabled → block navigation only
  if (btn.classList.contains('is-disabled')) {
    e.stopPropagation(); // 👈 stops product card navigation
    return;
  }
}, false); // 👈 NOT capture

    this.addEventListener('click', this.navigateToProduct);

this.addEventListener('pointerenter', () => {
  this.#syncHoverSizePicker();
});

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();

    this.syncFullviewSelectedColor();

    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

#syncHoverSizePicker() {
  const variantId = this.getSelectedVariantId();
  const hover = this.querySelector('.hover-size-picker');

  if (!hover || !variantId) return;

  const jsonEl = hover.querySelector('.product-variants-json');
  if (!jsonEl) return;

  const variants = JSON.parse(jsonEl.textContent);

  const currentVariant = variants.find(v => String(v.id) === String(variantId));
  if (!currentVariant) return;

  const selectedColor = currentVariant.option1;
  const selectedSize  = currentVariant.option2;

  const buttons = hover.querySelectorAll('.hover-size-picker__btn');

  const LOW_STOCK_THRESHOLD = 10;

  buttons.forEach(btn => {
    const size = btn.dataset.size;

    const match = variants.find(v =>
      v.option1 === selectedColor &&
      v.option2 === size
    );

    // reset
    btn.classList.remove('is-active', 'is-disabled', 'is-low-stock');
    btn.disabled = false;

    btn.querySelector('.low-stock-hover-wrapper')?.remove();

    if (!match) return;

    // sold out
    if (!match.available) {
      btn.classList.add('is-disabled');
      btn.disabled = true;
      return;
    }

    // low stock
    const stock = match.inventory_quantity ?? match.inventory ?? 0;

    if (stock > 0 && stock < LOW_STOCK_THRESHOLD) {
      btn.classList.add('is-low-stock');

      if (!btn.querySelector('.low-stock-hover-wrapper')) {
        btn.insertAdjacentHTML('beforeend', `
          <span class="low-stock-hover-wrapper">
            <span class="low-stock-hover-flash" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 16 16">
                <circle cx="8" cy="8" r="4" fill="#B89B73"/>
              </svg>
            </span>

            <span class="low-stock-hover-tooltip">
              Selling out
            </span>
          </span>
        `);
      }
    }

    // active
    if (size === selectedSize) {
      btn.classList.add('is-active');
    }
  });
}

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */

  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */

class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
  super.connectedCallback();

  this.parentProductCard = this.closest('product-card');

  this.addEventListener(
    ThemeEvents.variantUpdate,
    this.#handleCardVariantUrlUpdate.bind(this)
  );
}

disconnectedCallback() {
  super.disconnectedCallback();
}

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

document.addEventListener('click', async function(e) {
  const btn = e.target.closest('.hover-size-picker__btn');
  if (!btn) return;

  // ❌ ignore disabled
  if (btn.disabled || btn.classList.contains('is-disabled')) return;

  const productCard = btn.closest('product-card');
  if (!productCard) return;

  const hover = productCard.querySelector('.hover-size-picker');
  const jsonEl = hover?.querySelector('.product-variants-json');

  if (!jsonEl) return;

  const variants = JSON.parse(jsonEl.textContent);

  const selectedVariantId = productCard.getSelectedVariantId();
  if (!selectedVariantId) return;

  const currentVariant = variants.find(v => String(v.id) === String(selectedVariantId));
  if (!currentVariant) return;

  const selectedColor = currentVariant.option1;
  const clickedSize = btn.dataset.size;

  // ✅ find correct variant for (color + size)
  const match = variants.find(v =>
    v.option1 === selectedColor &&
    v.option2 === clickedSize
  );

  if (!match || !match.available) return;

  const variantId = match.id;

  // ✅ open cart immediately (fast UX)
  document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));

  try {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    });

    // ✅ update cart UI
    document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          data: {
            source: 'hover-quick-add',
            productVariantId: variantId
          }
        }
    }));

    // ✅ optional: visual feedback
    btn.classList.add('is-added');

    setTimeout(() => {
      btn.classList.remove('is-added');
    }, 800);

  } catch (err) {
    console.error('Hover add to cart failed:', err);
  }
});

document.addEventListener('mouseover', e => {
  const wrap = e.target.closest('.low-stock-hover-wrapper');
  if (!wrap) return;

  wrap.querySelector('.low-stock-hover-tooltip')?.classList.add('is-visible');
});

document.addEventListener('mouseout', e => {
  const wrap = e.target.closest('.low-stock-hover-wrapper');
  if (!wrap) return;

  wrap.querySelector('.low-stock-hover-tooltip')?.classList.remove('is-visible');
});